import { type User } from '@prisma/client'
import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Outlet,
	useLoaderData,
	useLocation,
	useNavigate,
	useSearchParams,
} from '@remix-run/react'
import { cellToBoundary, cellToLatLng, isValidCell, latLngToCell } from 'h3-js'
import { ArrowLeft, Maximize2Icon, Minimize2Icon } from 'lucide-react'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import Konami from 'react-konami-code'
import { type MapRef } from 'react-map-gl'

import { Toaster } from 'sonner'
import { AppNavigation } from '#app/components/app-navigation'
import { Header } from '#app/components/header'
import HydrationZustand from '#app/components/hydration-zustand'
import { Map } from '#app/components/map/map'
import { Button } from '#app/components/ui/button'
import { HIGHLIGHT_ZOOM, RESERVED_ROUTES } from '#app/consts'
import {
	getFavoritePlotGroupsByUserId,
	getFavoritePlotsByUserId,
} from '#app/models/favorite/get-favorite-plots'
import {
	getMediumClusterPlotIds,
	getPlotOrPlotGroupByHandle,
	getPlotsByResolution2,
	getPlotsByUserHandle,
	getPlotsByUserId,
	getSmallClusterPlotIds,
} from '#app/models/plot/get-plot'
import { StoreProvider, useStoreContext } from '#app/store'
import { type FetchedPlot, type FetchedPlotGroup } from '#app/types'
import { isAdminEmail } from '#app/utils/admin'
import { authenticator } from '#app/utils/auth.server'
import {
	useFlyToGroupPlot,
	useFlyToPlot,
	type FlySheetType,
} from '#app/utils/map'
import { cn } from '#app/utils/misc'
import { commitSession, getSession } from '#app/utils/session.server.js'

export const shouldRevalidate = () => false

type ViewState = {
	latitude: number
	longitude: number
	zoom: number
	bearing: number
	pitch: number
}

export type LayoutLoaderData = {
	user: User | null
	mapboxAccessToken: string
	googleMapsApiKey: string
	userPlots: FetchedPlot[]
	profilePlots: FetchedPlot[]
	viewportPlots: FetchedPlot[]
	favoritePlots: FetchedPlot[]
	favoritePlotGroups: FetchedPlotGroup[]
	smallClusterPlotIds: string[]
	mediumClusterPlotIds: string[]
	// largeClusterPlotIds: string[]
	initialViewState: ViewState
}

function getBoundsZoomAndCenter(plots: { plotId: string }[]) {
	if (plots.length === 0) return null
	if (plots.length === 1) {
		const coords = cellToBoundary(plots[0].plotId, true)[0]
		return {
			latitude: coords[1],
			longitude: coords[0],
			zoom: HIGHLIGHT_ZOOM,
		}
	}

	let minLat = 90
	let maxLat = -90
	let minLng = 180
	let maxLng = -180

	for (const plot of plots) {
		const coords = cellToBoundary(plot.plotId, true)[0] // [lng, lat]
		const lng = coords[0]
		const lat = coords[1]

		if (lat < minLat) minLat = lat
		if (lat > maxLat) maxLat = lat
		if (lng < minLng) minLng = lng
		if (lng > maxLng) maxLng = lng
	}

	const centerLat = (minLat + maxLat) / 2
	const centerLng = (minLng + maxLng) / 2

	const latDiff = maxLat - minLat
	const lngDiff = maxLng - minLng
	const maxDiff = Math.max(latDiff, lngDiff)

	let zoom = HIGHLIGHT_ZOOM
	if (maxDiff > 0) {
		zoom = Math.log2(360 / maxDiff) - 1.2
		zoom = Math.max(1, Math.min(HIGHLIGHT_ZOOM, zoom))
	}

	return {
		latitude: centerLat,
		longitude: centerLng,
		zoom: Math.round(zoom * 10) / 10,
	}
}

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const pathname = url.pathname
	const isHomePage = pathname === '/'

	// TODO (low) cache these?
	// const smallClusterPlotIds = await getAllSmallClusterPlotIds()
	let smallClusterPlotIds
	let mediumClusterPlotIds

	let centerPlot

	let profilePlots
	let userPlots
	let favoritePlots
	let favoritePlotGroups
	let viewportPlots

	const defaultPlot = '8a190dc72b4ffff' //nessie
	const defaultLatLng = cellToBoundary(defaultPlot, true)[0]

	// TODO (low) default to a plot instead
	// default to London
	let initialViewState = {
		latitude: defaultLatLng[1],
		longitude: defaultLatLng[0],
		zoom: HIGHLIGHT_ZOOM,
		bearing: 0,
		pitch: 0,
	}

	// 1. if on a plot page, center the map on the plot
	// check if the route is either /plot/<handle> or /plot/edit/<handle> or /plot/edit-group/<handle>
	const plotHandleRegex = /^\/plot\/([a-zA-Z0-9_]+)(?:\/edit)?$/
	const plotEditHandleRegex = /^\/plot\/edit\/([a-zA-Z0-9_]+)$/
	const plotEditGroupHandleRegex = /^\/plot\/edit-group\/([a-zA-Z0-9_]+)$/
	const plotHandleMatch =
		pathname.match(plotHandleRegex) ||
		pathname.match(plotEditHandleRegex) ||
		pathname.match(plotEditGroupHandleRegex)
	const isPlotPage = !!plotHandleMatch

	if (plotHandleMatch) {
		const plotHandle = plotHandleMatch[1]
		const plot = await getPlotOrPlotGroupByHandle(plotHandle)
		if (plot && 'plotId' in plot) {
			centerPlot = plot

			const center = cellToBoundary(plot.plotId, true)[0]
			initialViewState = {
				...initialViewState,
				latitude: center[1],
				longitude: center[0],
			}
		}

		if (plot && 'plots' in plot) {
			if (plot.plots.length > 0) {
				const center = cellToBoundary(plot.plots[0].plotId, true)[0]
				initialViewState = {
					...initialViewState,
					latitude: center[1],
					longitude: center[0],
				}
			}
		}
	}

	// 2. center the map on the profile plots' bounds if on a profile page
	const pathnameWithoutSlash = pathname.slice(1)
	const isProfilePage =
		!RESERVED_ROUTES.includes(pathnameWithoutSlash) && !isHomePage
	if (isProfilePage) {
		const profileRegex = /^\/([a-zA-Z0-9_]+)$/
		const profileMatch = pathname.match(profileRegex)

		if (profileMatch) {
			const userHandle = profileMatch[1]

			// get the owned plots for the profile
			profilePlots = (await getPlotsByUserHandle(userHandle)) as FetchedPlot[]
			if (profilePlots.length > 0) {
				const bounds = getBoundsZoomAndCenter(profilePlots)
				if (bounds) {
					initialViewState = {
						...initialViewState,
						latitude: bounds.latitude,
						longitude: bounds.longitude,
						zoom: bounds.zoom,
					}
				}
			}
		}
	}

	// Personal views start close to one owned place, rather than fitting a worldwide collection.
	const session = await authenticator.isAuthenticated(request)

	if (session) {
		userPlots = (await getPlotsByUserId(session.id)) as FetchedPlot[]
		const latestPlace = userPlots[0]
		const claimId = url.searchParams.get('plot')
		const focusId =
			pathname.startsWith('/claim') && claimId && isValidCell(claimId)
				? claimId
				: latestPlace?.plotId
		if (!isProfilePage && !isPlotPage && focusId && isValidCell(focusId)) {
			const [latitude, longitude] = cellToLatLng(focusId)
			initialViewState = {
				...initialViewState,
				latitude,
				longitude,
				zoom: HIGHLIGHT_ZOOM,
			}
		}

		const favoritePlotRelations = await getFavoritePlotsByUserId(session.id)
		favoritePlots = favoritePlotRelations.map(
			relation => relation.plot,
		) as FetchedPlot[]

		const favoritePlotGroupRelations = await getFavoritePlotGroupsByUserId(
			session.id,
		)
		favoritePlotGroups = favoritePlotGroupRelations.map(
			relation => relation.group,
		) as FetchedPlotGroup[]
	}

	if (centerPlot) {
		viewportPlots = await getPlotsByResolution2([centerPlot.resolution2PlotId])
	} else {
		const defaultCenter = latLngToCell(
			initialViewState.latitude,
			initialViewState.longitude,
			2,
		)
		viewportPlots = await getPlotsByResolution2([defaultCenter])
	}

	const allPlots = [
		...(userPlots || []),
		...(profilePlots || []),
		...(favoritePlots || []),
		...(viewportPlots || []),
	]

	const allResolution2Plots = Array.from(
		new Set(allPlots.map(plot => plot.resolution2PlotId)),
	)

	smallClusterPlotIds = await getSmallClusterPlotIds(allResolution2Plots)
	mediumClusterPlotIds = await getMediumClusterPlotIds(allResolution2Plots)

	// const signup = await fetch('https://app.thegoodapi.com/signup', {
	// 	method: 'POST',
	// 	headers: {
	// 		'Content-Type': 'application/json',
	// 		beta_token: 'KkeuYD2P6',
	// 	},
	// 	body: JSON.stringify({
	// 		email: 'chris@erda.app',
	// 		contact_name: 'Chris',
	// 		name: 'Erda',
	// 	}),
	// })

	// console.log(await signup.json())

	// 4. if not logged in, check if referred by a user
	let headers: HeadersInit = {}
	if (!session) {
		const referralCode = url.searchParams.get('rc')
		const cookieSession = await getSession(request.headers.get('Cookie'))

		if (referralCode) {
			cookieSession.set('referralCode', referralCode)

			headers = {
				'Set-Cookie': await commitSession(cookieSession),
			}
		}
	}

	return json<LayoutLoaderData>(
		{
			user: session,
			mapboxAccessToken: process.env.MAPBOX_TOKEN!,
			googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY!,
			initialViewState,
			userPlots: userPlots || [],
			profilePlots: profilePlots || [],
			favoritePlots: favoritePlots || [],
			favoritePlotGroups: favoritePlotGroups || [],
			viewportPlots: viewportPlots || [],
			smallClusterPlotIds:
				(smallClusterPlotIds?.map(
					plot => plot.resolution7PlotId,
				) as string[]) || [],

			mediumClusterPlotIds:
				(mediumClusterPlotIds?.map(
					plot => plot.resolution5PlotId,
				) as string[]) || [],
		},
		{
			headers,
		},
	)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => [
	{ title: 'Erda — claim a free place and share your story' },
	{
		name: 'description',
		content: `Claim your first place on Erda’s map for free and share its story. Additional places and gifts cost $2/year and help fund trees.`,
	},
	{
		name: 'keywords',
		content:
			'erda, app, own, gift, digital, places, piece, world, map, plant, tree, helps, hunger, poverty, environmental, sustainability, sentimental, meaningful, unique, instant',
	},
	{
		name: 'og:title',
		content: 'Erda — claim a free place and share your story',
	},
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: 'https://erda.app/image/home.png',
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Claim your first place on Erda’s map for free and share its story. Additional places and gifts cost $2/year and help fund trees.`,
	},
	{
		'twitter:image': 'https://erda.app/image/home.png',
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{
		name: 'twitter:title',
		content: 'Erda — claim a free place and share your story',
	},
	{
		name: 'twitter:description',
		content: `Claim your first place on Erda’s map for free and share its story. Additional places and gifts cost $2/year and help fund trees.`,
	},
]

export type LayoutContextType = {
	mapRef: React.MutableRefObject<MapRef | null>
	flyToPlot: (plot: string, sheetType?: FlySheetType) => void
	flyToGroupPlot: (groupId: string, sheetType?: FlySheetType) => void
}

const panelTitles: Record<string, string> = {
	'/': 'Welcome to Erda',
	'/digital-gift': 'Give a meaningful place',
	'/info': 'About Erda',
	'/tree-planting': 'Tree planting',
	'/account': 'Your account',
	'/admin': 'Business overview',
	'/account/gifts': 'Your gifts',
	'/basket': 'Your selected places',
	'/gift-basket': 'Your gift',
	'/leaderboard': 'Community impact',
	'/login': 'Welcome to Erda',
	'/contact': 'Get in touch',
	'/privacy': 'Privacy',
	'/terms': 'Terms',
	'/own-something-unique': 'Sponsor a place',
	'/checkout': 'Checkout',
	'/claim': 'Your selected place',
	'/claim/saved': 'Your free place',
}

export default function Layout() {
	const location = useLocation()

	const {
		mapboxAccessToken,
		userPlots,
		profilePlots,
		favoritePlots,
		favoritePlotGroups,
		viewportPlots,
		smallClusterPlotIds,
		mediumClusterPlotIds,
		initialViewState,
		user,
	} = useLoaderData() as LayoutLoaderData

	useEffect(() => {
		if (!user) return

		posthog.identify(user.id, {
			email: user.email,
			name: user.name,
			handle: user.handle,
			created_at: user.createdAt,
		})
	}, [user])

	// remove any duplicate plots. consider any plot with the same plotId as a duplicate
	const combinedPlots = [
		...userPlots,
		...profilePlots,
		...favoritePlots,
		...viewportPlots,
	]
	const loadedPlots = Array.from(
		new Set(combinedPlots.map(plot => plot.plotId)),
	).map(plotId => {
		return combinedPlots.find(plot => plot.plotId === plotId)!
	})

	const isPlot =
		location.pathname.includes('/plot/') && !location.pathname.includes('/edit')
	const shouldCollapseSheet =
		isPlot || location.pathname === '/claim' || location.pathname === '/'

	const canRenderOutlet = [
		'/',
		'/info',
		'/digital-gift',
		'/own-something-unique',
		'/contact',
		'/leaderboard',
		'/tree-planting',
		'/terms',
		'/privacy',
	].includes(location.pathname)

	return (
		<HydrationZustand
			fallback={
				!(
					location.pathname === '/' &&
					new URLSearchParams(location.search).get('map') === '1'
				) ? (
					<div className="app-shell">
						<AppNavigation
							selectedCount={0}
							isAdmin={isAdminEmail(user?.email)}
						/>
						<section
							className={cn(
								'app-page-panel',
								shouldCollapseSheet && 'app-page-panel-compact',
								location.pathname === '/' && 'app-welcome-panel',
							)}
						>
							<header className="flex min-h-16 shrink-0 items-center gap-3 border-b px-5 py-3">
								<Button variant="ghost" size="icon" aria-label="Back to map">
									<ArrowLeft className="h-5 w-5" />
								</Button>
								<p className="flex-1 text-base font-extrabold">
									{panelTitles[location.pathname] ?? 'Your place on Erda'}
								</p>
								{shouldCollapseSheet && (
									<Button
										variant="ghost"
										size="icon"
										className="md:hidden"
										aria-label="Expand panel"
									>
										<Maximize2Icon className="h-4 w-4" />
									</Button>
								)}
							</header>
							<div className="app-page-body flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-5 py-6 md:px-8 md:py-8">
								{canRenderOutlet ? <Outlet /> : null}
							</div>
						</section>
					</div>
				) : null
			}
		>
			<StoreProvider
				loadedPlots={loadedPlots}
				smallClusterPlotIds={smallClusterPlotIds}
				mediumClusterPlotIds={mediumClusterPlotIds}
				viewState={{
					...initialViewState,
					padding: {
						top: 0,
						bottom: 0,
						left: 0,
						right: 0,
					},
				}}
				favoritePlotIds={favoritePlots.map(plot => plot.plotId)}
				favoritePlotGroupIds={
					favoritePlotGroups.map(group => group.id) as string[]
				}
				isSheetExpanded={!shouldCollapseSheet}
			>
				<LayoutInner
					{...{ mapboxAccessToken, initialViewState }}
					isAdmin={isAdminEmail(user?.email)}
				/>
			</StoreProvider>
		</HydrationZustand>
	)
}

export function LayoutInner({
	mapboxAccessToken,
	isAdmin = false,
}: {
	mapboxAccessToken: string
	isAdmin?: boolean
}) {
	const navigate = useNavigate()
	const location = useLocation()

	const [searchParams] = useSearchParams()
	const screenshot = searchParams.get('screenshot')

	const debugMode = useStoreContext(s => s.debugMode)
	const setDebugMode = useStoreContext(s => s.setDebugMode)
	const setViewStatePadding = useStoreContext(s => s.setViewStatePadding)
	const setHighlightedPlotIds = useStoreContext(s => s.setHighlightedPlotIds)
	const setHighlightedPlotGroupIds = useStoreContext(
		s => s.setHighlightedPlotGroupIds,
	)
	const isSheetExpanded = useStoreContext(s => s.isSheetExpanded)
	const setIsSheetExpanded = useStoreContext(s => s.setIsSheetExpanded)

	const clearHighlighted = useCallback(() => {
		setHighlightedPlotIds([])
		setHighlightedPlotGroupIds([])
	}, [setHighlightedPlotGroupIds, setHighlightedPlotIds])

	const [welcomeDismissed, setWelcomeDismissed] = useState(false)
	const isWelcome =
		location.pathname === '/' &&
		searchParams.get('map') !== '1' &&
		!welcomeDismissed
	const isHome = location.pathname === '/' && !isWelcome
	const isCheckout = location.pathname === '/checkout'
	const isPlot =
		location.pathname === '/claim' ||
		(location.pathname.startsWith('/plot/') &&
			!location.pathname.includes('/edit') &&
			!location.pathname.includes('/create-group'))
	const [viewport, setViewport] = useState({ width: 1024, height: 768 })
	const isMobile = viewport.width < 768
	const panelHeading = useRef<HTMLParagraphElement>(null)
	const panelBody = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const update = () =>
			setViewport({ width: window.innerWidth, height: window.innerHeight })
		update()
		window.addEventListener('resize', update)
		return () => window.removeEventListener('resize', update)
	}, [])

	useEffect(() => {
		setIsSheetExpanded(!isPlot && !isWelcome)
		panelBody.current?.scrollTo(0, 0)
		panelHeading.current?.focus({ preventScroll: true })
	}, [location.pathname, isPlot, isWelcome, setIsSheetExpanded])

	const onClose = useCallback(() => {
		if (isCheckout) return
		if (isWelcome) {
			setWelcomeDismissed(true)
			return
		}
		clearHighlighted()
		navigate('/?map=1')
	}, [isCheckout, isWelcome, clearHighlighted, navigate])

	const title =
		panelTitles[location.pathname] ??
		(location.pathname.startsWith('/gift/')
			? 'Your gift'
			: isPlot
				? 'A place on the map'
				: 'Your place on Erda')
	const mapRef = useRef<MapRef>(null)

	const flyToGroupPlot = useFlyToGroupPlot()
	const flyToPlot = useFlyToPlot()

	useEffect(() => {
		setViewStatePadding({
			top: 0,
			bottom:
				isMobile && !isHome && !isSheetExpanded ? viewport.height * 0.48 : 0,
			left:
				!isMobile && !isHome && !screenshot
					? Math.min(560, viewport.width * 0.48)
					: 0,
			right: 0,
		})
	}, [
		isMobile,
		viewport,
		isHome,
		isPlot,
		isSheetExpanded,
		screenshot,
		setViewStatePadding,
	])

	const onToggleSheetExpanded = useCallback(() => {
		setIsSheetExpanded(!isSheetExpanded)
		posthog.capture('toggle_sheet_expanded', { expanded: !isSheetExpanded })
	}, [isSheetExpanded, setIsSheetExpanded])

	return (
		<div
			className={cn(
				'app-shell',
				screenshot && 'app-shell-screenshot screenshot h-[1080px]',
			)}
		>
			{!screenshot && <Header isAdmin={isAdmin} />}
			<div
				className={cn('app-map', !isHome && 'app-map-with-panel')}
				aria-label="Explore the world"
				{...(isMobile && !isHome && isSheetExpanded && !screenshot
					? { inert: '', 'aria-hidden': true as const }
					: {})}
			>
				<Map
					mapRef={mapRef}
					mapboxAccessToken={mapboxAccessToken}
					showSelectionPrompt={isHome || !isMobile}
				/>
			</div>
			{!screenshot && (
				<>
					{isHome && (
						<aside className="map-claim-prompt" aria-label="Choose a place">
							<div className="map-claim-prompt-copy">
								<h2>
									{searchParams.get('select') === 'paid' ? (
										'Make room for another story.'
									) : (
										<>
											Your first place is <em>free.</em>
										</>
									)}
								</h2>
								<p>
									{searchParams.get('select') === 'paid'
										? 'Choose an available place. $2/year each.'
										: 'Zoom in and tap an available place.'}
								</p>
								<button
									className="map-claim-prompt-link"
									onClick={() => {
										setWelcomeDismissed(false)
										navigate('/')
									}}
								>
									How it works <span aria-hidden="true">↗</span>
								</button>
							</div>
							<span className="map-claim-prompt-price" aria-hidden="true">
								{searchParams.get('select') === 'paid' ? '$2' : '$0'}
							</span>
						</aside>
					)}
					{!isHome && (
						<section
							aria-labelledby="page-panel-title"
							className={cn(
								'app-page-panel',
								!isSheetExpanded && 'app-page-panel-compact',
								isWelcome && 'app-welcome-panel',
							)}
							onKeyDown={event => {
								if (
									event.key === 'Escape' &&
									event.target === panelHeading.current
								)
									onClose()
							}}
						>
							<header className="flex min-h-16 shrink-0 items-center gap-3 border-b px-5 py-3">
								{!isCheckout && (
									<Button
										variant="ghost"
										size="icon"
										onClick={onClose}
										aria-label="Back to map"
									>
										<ArrowLeft className="h-5 w-5" />
									</Button>
								)}
								<p
									id="page-panel-title"
									ref={panelHeading}
									tabIndex={-1}
									className="flex-1 text-base font-extrabold outline-none"
								>
									{title}
								</p>
								{(isPlot || !isSheetExpanded) && (
									<Button
										variant="ghost"
										size="icon"
										className="md:hidden"
										onClick={onToggleSheetExpanded}
										aria-label={
											isSheetExpanded ? 'Show more of the map' : 'Expand panel'
										}
									>
										{isSheetExpanded ? (
											<Minimize2Icon className="h-4 w-4" />
										) : (
											<Maximize2Icon className="h-4 w-4" />
										)}
									</Button>
								)}
							</header>
							<div
								ref={panelBody}
								className="app-page-body flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-5 py-6 md:px-8 md:py-8"
							>
								<Outlet
									context={{
										onExplore: () => setWelcomeDismissed(true),
										mapRef,
										flyToPlot: (plotId: string, sheetState?: FlySheetType) =>
											flyToPlot(plotId, mapRef, sheetState),
										flyToGroupPlot: (
											groupId: string,
											sheetState?: FlySheetType,
										) => flyToGroupPlot(groupId, mapRef, sheetState),
									}}
								/>
							</div>
						</section>
					)}
					<Toaster position="top-center" />
					<Konami action={() => setDebugMode(!debugMode)} />
				</>
			)}
		</div>
	)
}
